// Logo-page bespoke blocks.
// Extracted from the (previously monolithic) BlockRenderer so each component
// can be read and iterated on without scrolling past 1000 lines of unrelated
// code. Keeps the public surface tiny: 5 named renderer exports that the
// main BlockRenderer switch dispatches on.
import { useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import api, { fileUrl, API } from "@/lib/api";
import RichText from "@/components/brand/RichText";
import { Input } from "@/components/ui/input";
import { Download, Upload, UploadCloud, X, Trash2, Plus } from "lucide-react";

const dashed = "bh-editable-block rounded-xl border border-dashed border-gray-200 p-5 focus-within:border-[var(--bh-accent,#fe7f57)] transition-colors";

export const LOGO_UPLOAD_ACCEPT = ".eps,.pdf,.svg,image/svg+xml,application/pdf,application/postscript";
export const AVATAR_UPLOAD_ACCEPT = ".png,.svg,.eps,.pdf,image/png,image/svg+xml,application/pdf,application/postscript";
const LOGO_DOWNLOAD_EXTS = ["png", "svg", "eps"];

const detectExt = (content) => {
  const ct = (content?.content_type || "").toLowerCase();
  if (ct.includes("svg")) return "svg";
  if (ct.includes("pdf")) return "pdf";
  if (ct.includes("postscript") || ct.includes("eps")) return "eps";
  const fn = (content?.filename || "").toLowerCase();
  const m = fn.match(/\.([a-z0-9]+)$/);
  return m ? m[1] : "";
};

async function detectLogoBgColor(file) {
  try {
    if (!file || !file.type || !file.type.startsWith("image/") || file.type === "image/svg+xml") {
      return "#f2f1ed";
    }
    const url = URL.createObjectURL(file);
    const img = await new Promise((res, rej) => {
      const i = new Image();
      i.onload = () => res(i);
      i.onerror = rej;
      i.src = url;
    });
    const w = Math.min(img.width, 80);
    const h = Math.min(img.height, 80);
    const canvas = document.createElement("canvas");
    canvas.width = w; canvas.height = h;
    const ctx = canvas.getContext("2d");
    ctx.drawImage(img, 0, 0, w, h);
    const { data } = ctx.getImageData(0, 0, w, h);
    URL.revokeObjectURL(url);
    let sum = 0, count = 0;
    for (let i = 0; i < data.length; i += 4) {
      if (data[i + 3] < 50) continue;
      const lum = (0.299 * data[i] + 0.587 * data[i + 1] + 0.114 * data[i + 2]) / 255;
      sum += lum; count += 1;
    }
    const avg = count ? sum / count : 0.5;
    return avg < 0.55 ? "#f2f1ed" : "#1c1c1c";
  } catch {
    return "#f2f1ed";
  }
}

// Server-side converter — gs + pstoedit on the backend turn uploaded SVGs or
// EPSes into the format the user clicked. Buttons fall back to a browser
// canvas conversion only when the server returns a non-200.
function convertUrl(fileId, ext) {
  const token = typeof localStorage !== "undefined" ? localStorage.getItem("bh_token") : null;
  const qs = new URLSearchParams({ to: ext });
  if (token) qs.set("auth", token);
  return `${API}/files/${fileId}/convert?${qs.toString()}`;
}

async function triggerDownload(url, filename) {
  const res = await fetch(url);
  if (!res.ok) throw new Error("Download failed");
  const blob = await res.blob();
  const objUrl = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = objUrl;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  setTimeout(() => URL.revokeObjectURL(objUrl), 1000);
}

function LogoBlockHeader({ c, editMode, titlePlaceholder, textPlaceholder, onUpdate }) {
  if (!editMode) {
    return (
      <div className="grid grid-cols-1 md:grid-cols-[260px_1fr] gap-10 mb-8">
        <h3 className="font-display text-xl md:text-[22px] font-medium tracking-tight text-[#1a1a1a] self-start">{c.title}</h3>
        {c.text ? <div className="text-[15px] md:text-base leading-relaxed text-[#1a1a1a] bh-rt-view" dangerouslySetInnerHTML={{ __html: c.text }} /> : <div />}
      </div>
    );
  }
  return (
    <div className="grid grid-cols-1 md:grid-cols-[260px_1fr] gap-10 mb-6 items-start">
      <input
        value={c.title || ""}
        onChange={(e) => onUpdate({ title: e.target.value })}
        className="w-full font-display text-xl md:text-[22px] font-medium tracking-tight bg-transparent border-none outline-none leading-7 p-0 h-7 self-start"
        placeholder={titlePlaceholder}
      />
      <RichText
        value={c.text || ""}
        onChange={(html) => onUpdate({ text: html })}
        placeholder={textPlaceholder || "Uitleg…"}
        className="w-full min-h-7 text-[15px] leading-7 outline-none self-start"
      />
    </div>
  );
}

// --- Shared helper used by Vrije ruimte, Kleurgebruik, Don'ts ----------------
function usePrimaryLogo(pageBlocks) {
  const match = (pageBlocks || []).find(
    (b) => b.type === "logo_variant" && b.content?.file_id && b.content?.variant === "primary"
  ) || (pageBlocks || []).find(
    (b) => b.type === "logo_variant" && b.content?.file_id
  );
  const fileId = match?.content?.file_id || null;
  const src = fileId ? fileUrl(fileId) : null;
  const ext = detectExt(match?.content || {});
  return { fileId, src, isSvg: ext === "svg", ext };
}

// ---------- Steps 1 & 2 + drawer "Extra logo variant" ----------
export function LogoVariantBlock({ block, editMode, brand, onUpdate }) {
  const c = block.content || {};
  const inputRef = useRef();
  const [busy, setBusy] = useState(false);
  const [drag, setDrag] = useState(false);

  const upload = async (file) => {
    if (!file) return;
    setBusy(true);
    try {
      const [bgColor, uploadRes] = await Promise.all([
        detectLogoBgColor(file),
        (async () => {
          const fd = new FormData();
          fd.append("file", file);
          return api.post(`/upload?brand_id=${brand.id}`, fd, { headers: { "Content-Type": "multipart/form-data" } });
        })(),
      ]);
      onUpdate({ file_id: uploadRes.data.file_id, filename: uploadRes.data.filename, content_type: uploadRes.data.content_type, bg_color: bgColor });
    } catch { toast.error("Uploaden mislukt"); }
    finally { setBusy(false); }
  };

  const bg = c.bg_color || "#f2f1ed";
  const src = c.file_id ? fileUrl(c.file_id) : null;
  const titleGuess = c.variant === "secondary" ? "Secundaire logo" : "Primaire logo";
  const uploaded = detectExt(c);

  const handleDownload = async (ext) => {
    const base = (c.title || "logo").toLowerCase().replace(/\s+/g, "-");
    try {
      if (ext === uploaded) {
        await triggerDownload(fileUrl(c.file_id, { download: true }), `${base}.${ext}`);
      } else {
        await triggerDownload(convertUrl(c.file_id, ext), `${base}.${ext}`);
      }
    } catch { toast.error(`Download .${ext} mislukt`); }
  };

  return (
    <div className={editMode ? dashed : ""} data-testid={`logo-variant-${block.id}`}>
      <LogoBlockHeader c={c} editMode={editMode} titlePlaceholder={titleGuess} textPlaceholder="Waar en hoe gebruik je dit logo?" onUpdate={onUpdate} />
      <div
        onDragOver={(e) => { if (!editMode) return; e.preventDefault(); setDrag(true); }}
        onDragLeave={() => setDrag(false)}
        onDrop={(e) => { if (!editMode) return; e.preventDefault(); setDrag(false); upload(e.dataTransfer.files?.[0]); }}
        className={`relative rounded-xl flex items-center justify-center min-h-[280px] md:min-h-[320px] transition-colors ${editMode && drag ? "ring-2 ring-black/40" : ""}`}
        style={{ background: bg }}
      >
        {src ? (
          <img src={src} alt={c.title || "logo"} className="max-h-56 max-w-[50%] object-contain" />
        ) : editMode ? (
          <button onClick={() => inputRef.current?.click()} className="flex flex-col items-center gap-2 text-sm text-[#1a1a1a]/60 hover:text-[#1a1a1a]">
            <UploadCloud className="w-6 h-6" />
            <span>Sleep een logo hierheen of klik om te uploaden</span>
            <span className="text-xs text-[#1a1a1a]/40">.eps · .pdf · .svg</span>
          </button>
        ) : (
          <div className="min-h-[260px]" aria-hidden="true" />
        )}
        {editMode && (
          <>
            <input ref={inputRef} type="file" hidden accept={LOGO_UPLOAD_ACCEPT} onChange={(e) => upload(e.target.files?.[0])} />
            {src && (
              <button
                onClick={() => inputRef.current?.click()}
                disabled={busy}
                className="absolute top-3 right-3 flex items-center gap-1.5 px-3 h-8 rounded-full bg-white/95 border border-gray-300 text-xs"
                data-testid={`logo-variant-upload-${block.id}`}
              >
                <Upload className="w-3.5 h-3.5" /> {busy ? "Bezig…" : "Vervang"}
              </button>
            )}
          </>
        )}
      </div>
      {src && (
        <div className="mt-5 flex flex-wrap gap-2">
          {LOGO_DOWNLOAD_EXTS.map((ext) => (
            <button
              key={ext}
              type="button"
              onClick={() => handleDownload(ext)}
              data-testid={`logo-variant-download-${ext}-${block.id}`}
              className="inline-flex items-center gap-2 px-4 h-9 rounded-md text-sm font-medium bg-black text-white hover:bg-black/90"
            >
              <Download className="w-4 h-4" /> .{ext}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

// ---------- Step 3: Avatar grid ----------
export function LogoAvatarBlock({ block, editMode, brand, onUpdate }) {
  const c = block.content || {};
  const items = Array.isArray(c.items) ? c.items : [];
  const newId = () => (crypto.randomUUID ? crypto.randomUUID() : Math.random().toString(36).slice(2));

  const addItem = () => onUpdate({ items: [...items, { id: newId(), file_id: null, description: "" }] });
  const updateItem = (idx, patch) => onUpdate({ items: items.map((it, i) => (i === idx ? { ...it, ...patch } : it)) });
  const removeItem = (idx) => onUpdate({ items: items.filter((_, i) => i !== idx) });

  return (
    <div className={editMode ? dashed : ""} data-testid={`logo-avatar-${block.id}`}>
      <LogoBlockHeader c={c} editMode={editMode} titlePlaceholder="Avatar" textPlaceholder="Hoe zet je de avatar in?" onUpdate={onUpdate} />
      <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
        {items.map((it, idx) => (
          <AvatarCard key={it.id || idx} item={it} brand={brand} editMode={editMode} onUpdate={(p) => updateItem(idx, p)} onRemove={() => removeItem(idx)} testId={`logo-avatar-card-${block.id}-${idx}`} />
        ))}
        {editMode && (
          <button
            onClick={addItem}
            data-testid={`logo-avatar-add-${block.id}`}
            className="flex flex-col items-center justify-center gap-2 min-h-[260px] rounded-xl border-2 border-dashed border-gray-300 text-gray-400 hover:text-gray-600 hover:border-gray-400 text-sm"
          >
            <Plus className="w-5 h-5" /> Avatar toevoegen
          </button>
        )}
      </div>
    </div>
  );
}

function AvatarCard({ item, brand, editMode, onUpdate, onRemove, testId }) {
  const inputRef = useRef();
  const [busy, setBusy] = useState(false);
  const upload = async (file) => {
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post(`/upload?brand_id=${brand.id}`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      onUpdate({ file_id: data.file_id, filename: data.filename });
    } catch { toast.error("Uploaden mislukt"); }
    finally { setBusy(false); }
  };
  const src = item.file_id ? fileUrl(item.file_id) : null;
  return (
    <div className="rounded-xl bg-gray-50 border border-gray-200 overflow-hidden flex flex-col" data-testid={testId}>
      <div className={`relative aspect-square flex items-center justify-center ${src ? "bg-white" : (editMode ? "bg-[#f8f8f6]" : "bg-white")}`}>
        {src ? (
          <img src={src} alt={item.description || "avatar"} className="w-full h-full object-contain" />
        ) : editMode ? (
          <button onClick={() => inputRef.current?.click()} className="flex flex-col items-center gap-1 text-xs text-gray-500 hover:text-gray-700 px-4 text-center">
            <UploadCloud className="w-5 h-5" />
            <span>Upload avatar</span>
            <span className="text-[11px] text-gray-400">Aanbevolen: 600×600px</span>
          </button>
        ) : (
          <div className="text-xs text-gray-400">Geen avatar</div>
        )}
        {editMode && (
          <>
            <input ref={inputRef} type="file" hidden accept={AVATAR_UPLOAD_ACCEPT} onChange={(e) => upload(e.target.files?.[0])} />
            {src && (
              <button onClick={() => inputRef.current?.click()} disabled={busy} className="absolute top-2 right-2 px-2 h-7 rounded-md bg-white/95 border border-gray-300 text-xs">
                {busy ? "Bezig…" : "Vervang"}
              </button>
            )}
            <button onClick={onRemove} className="absolute top-2 left-2 w-7 h-7 rounded-md bg-white/95 border border-gray-300 text-gray-500 hover:text-red-600 flex items-center justify-center">
              <Trash2 className="w-3.5 h-3.5" />
            </button>
          </>
        )}
      </div>
      <div className="px-4 py-3">
        {editMode ? (
          <Input value={item.description || ""} onChange={(e) => onUpdate({ description: e.target.value })} className="bg-white border-gray-200 text-sm" placeholder="Beschrijving" />
        ) : (
          <div className="text-sm text-[#1a1a1a]/70">{item.description || ""}</div>
        )}
      </div>
    </div>
  );
}

// ---------- Step 4: Clear space tool ----------
export function LogoClearSpaceBlock({ block, editMode, pageBlocks, onUpdate }) {
  const c = block.content || {};
  const { src, isSvg, fileId } = usePrimaryLogo(pageBlocks);
  const unit = ["px", "mm", "%"].includes(c.unit) ? c.unit : "px";
  // Measure the actual *painted* bounds of the logo inside the preview box so
  // the dashed guides hug the SVG/PNG content shape — not the fixed container.
  // For SVG we take getBBox() in user units and project it through
  // getScreenCTM() so letter-boxed viewBoxes still produce correct coords.
  // For raster we use naturalWidth/Height + "object-contain" math.
  const logoRef = useRef(null);
  const [contentBox, setContentBox] = useState(null); // {left, top, width, height} within logoRef
  useEffect(() => {
    const container = logoRef.current;
    if (!container) return undefined;
    let raf = 0;
    const measure = () => {
      const cRect = container.getBoundingClientRect();
      if (!cRect.width || !cRect.height) return;
      const svgEl = container.querySelector("svg");
      const imgEl = container.querySelector("img");
      if (svgEl) {
        try {
          const bbox = svgEl.getBBox();
          const ctm = svgEl.getScreenCTM();
          if (!ctm || !bbox.width || !bbox.height) return;
          const pt = svgEl.createSVGPoint();
          pt.x = bbox.x; pt.y = bbox.y;
          const tl = pt.matrixTransform(ctm);
          pt.x = bbox.x + bbox.width; pt.y = bbox.y + bbox.height;
          const br = pt.matrixTransform(ctm);
          setContentBox({
            left: tl.x - cRect.left,
            top: tl.y - cRect.top,
            width: br.x - tl.x,
            height: br.y - tl.y,
          });
        } catch { /* not laid out yet — mutation observer will retry */ }
      } else if (imgEl && imgEl.naturalWidth && imgEl.naturalHeight) {
        const nw = imgEl.naturalWidth, nh = imgEl.naturalHeight;
        const scale = Math.min(cRect.width / nw, cRect.height / nh);
        const w = nw * scale, h = nh * scale;
        setContentBox({
          left: (cRect.width - w) / 2,
          top: (cRect.height - h) / 2,
          width: w,
          height: h,
        });
      }
    };
    const schedule = () => { cancelAnimationFrame(raf); raf = requestAnimationFrame(measure); };
    schedule();
    const ro = new ResizeObserver(schedule);
    ro.observe(container);
    // SVG DOM is injected after the fetch resolves — re-measure on mutations.
    const mo = new MutationObserver(schedule);
    mo.observe(container, { childList: true, subtree: true, attributes: true });
    const img = container.querySelector("img");
    if (img) img.addEventListener("load", schedule);
    return () => {
      cancelAnimationFrame(raf);
      ro.disconnect();
      mo.disconnect();
      if (img) img.removeEventListener("load", schedule);
    };
  }, [src, isSvg, fileId]);
  const refW = contentBox?.width || 160;
  const refH = contentBox?.height || 160;
  const toPx = (v, axis) => {
    const n = Number(v || 0);
    if (unit === "mm") return n * 3.78;
    if (unit === "%") return (n / 100) * (axis === "x" ? refW : refH);
    return n;
  };
  const top = toPx(c.top, "y"), right = toPx(c.right, "x"), bottom = toPx(c.bottom, "y"), left = toPx(c.left, "x");

  const field = (key, label) => (
    <div className="flex flex-col gap-1">
      <label className="text-[11px] uppercase tracking-wide text-gray-500">{label}</label>
      <input
        type="number"
        min="0"
        max={unit === "%" ? 200 : undefined}
        step={unit === "%" ? 1 : (unit === "mm" ? 0.5 : 1)}
        value={c[key] ?? 0}
        onChange={(e) => onUpdate({ [key]: Number(e.target.value) })}
        className="h-9 px-3 rounded-md border border-gray-200 bg-white text-sm w-full"
        data-testid={`clear-space-${key}-${block.id}`}
      />
    </div>
  );

  const guideLabel = (val) => `${val ?? 0}${unit}`;

  // Shared pill-style label used in the margin area between the inner (tight)
  // and outer (clear-space) dashed guides. Pill sits on top of the dashed line
  // so the dashed stroke appears to be “measured” by the number.
  const marginLabel = (val, pos, testId) => (
    <span
      className="absolute text-[10px] text-gray-600 font-mono bg-[#f8f8f6] px-1 leading-none pointer-events-none"
      style={pos}
      data-testid={testId}
    >
      {guideLabel(val)}
    </span>
  );

  const preview = src ? (
    <div className="relative inline-flex">
      {contentBox && (
        <>
          {/* Outer dashed guide — sits at the specified clear-space margins from the content */}
          <div
            className="absolute pointer-events-none border border-dashed border-gray-400"
            style={{
              left: contentBox.left - left,
              top: contentBox.top - top,
              width: contentBox.width + left + right,
              height: contentBox.height + top + bottom,
            }}
          />
          {/* Inner dashed guide — hugs the real painted bounds of the SVG/PNG */}
          <div
            className="absolute pointer-events-none border border-dashed border-gray-400"
            style={{
              left: contentBox.left,
              top: contentBox.top,
              width: contentBox.width,
              height: contentBox.height,
            }}
          />
          {/* Distance labels sit INSIDE the clear-space margin (between inner and outer lines) */}
          {top > 0 && marginLabel(c.top, {
            left: contentBox.left + contentBox.width / 2,
            top: contentBox.top - top / 2,
            transform: "translate(-50%, -50%)",
          }, "clear-space-label-top")}
          {bottom > 0 && marginLabel(c.bottom, {
            left: contentBox.left + contentBox.width / 2,
            top: contentBox.top + contentBox.height + bottom / 2,
            transform: "translate(-50%, -50%)",
          }, "clear-space-label-bottom")}
          {left > 0 && marginLabel(c.left, {
            left: contentBox.left - left / 2,
            top: contentBox.top + contentBox.height / 2,
            transform: "translate(-50%, -50%)",
          }, "clear-space-label-left")}
          {right > 0 && marginLabel(c.right, {
            left: contentBox.left + contentBox.width + right / 2,
            top: contentBox.top + contentBox.height / 2,
            transform: "translate(-50%, -50%)",
          }, "clear-space-label-right")}
        </>
      )}
      <div ref={logoRef} className="w-[320px] h-[200px] flex items-center justify-center">
        {isSvg
          ? <TintedSvgLogo fileId={fileId} color="" className="w-full h-full" />
          : <img src={src} alt="Primaire logo" className="max-w-full max-h-full object-contain" />}
      </div>
    </div>
  ) : (
    <div className="text-sm text-gray-400">Upload eerst de Primaire logo om de vrije ruimte te zien.</div>
  );

  return (
    <div className={editMode ? dashed : ""} data-testid={`logo-clear-space-${block.id}`}>
      <LogoBlockHeader c={c} editMode={editMode} titlePlaceholder="Vrije ruimte" textPlaceholder="Hoeveel witruimte moet er rond het logo blijven?" onUpdate={onUpdate} />
      <div className="rounded-xl bg-[#f8f8f6] border border-gray-200 py-16 px-20 flex items-center justify-center min-h-[380px]">
        {preview}
      </div>
      {editMode && (
        <div className="mt-5 grid grid-cols-2 md:grid-cols-5 gap-3 items-end">
          {field("top", "Boven")}
          {field("right", "Rechts")}
          {field("bottom", "Onder")}
          {field("left", "Links")}
          <div className="flex flex-col gap-1">
            <label className="text-[11px] uppercase tracking-wide text-gray-500">Eenheid</label>
            <div className="h-9 inline-flex items-center rounded-md bg-gray-100 p-0.5 text-xs">
              {["px", "mm", "%"].map((u) => (
                <button
                  key={u}
                  onClick={() => onUpdate({ unit: u })}
                  data-testid={`clear-space-unit-${u}-${block.id}`}
                  className={`flex-1 h-8 rounded px-2 ${unit === u ? "bg-white shadow-sm font-medium" : "text-gray-500"}`}
                >{u}</button>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ---------- Step 5: Color usage ----------
export function LogoColorUsageBlock({ block, editMode, brand, pageBlocks, onUpdate }) {
  const c = block.content || {};
  const items = Array.isArray(c.items) ? c.items : [];
  const newId = () => (crypto.randomUUID ? crypto.randomUUID() : Math.random().toString(36).slice(2));
  const [colors, setColors] = useState([]);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/brands/${brand.id}/pages`);
        const kleuren = (data || []).find((p) => p.name === "Kleuren" || p.name === "Colors");
        const palette = kleuren?.blocks?.find((b) => b.type === "color_palette");
        // Flatten solid colours + any configured gradients onto a single swatch list.
        const solids = (palette?.content?.colors || []).map((x) => ({ name: x.name, hex: x.hex }));
        const gradients = (palette?.content?.gradients || []).map((g) => ({
          name: g.name || "Gradient",
          hex: typeof g === "string" ? g : (g.css || g.value || g.hex),
        })).filter((g) => g.hex);
        setColors([...solids, ...gradients]);
      } catch { setColors([]); }
      finally { setLoaded(true); }
    })();
  }, [brand?.id]);

  const { src, isSvg, fileId } = usePrimaryLogo(pageBlocks);

  const addItem = () => onUpdate({ items: [...items, { id: newId(), logo_color: "", bg_color: "" }] });
  const updateItem = (idx, patch) => onUpdate({ items: items.map((it, i) => (i === idx ? { ...it, ...patch } : it)) });
  const removeItem = (idx) => onUpdate({ items: items.filter((_, i) => i !== idx) });

  const noColors = loaded && colors.length === 0;

  return (
    <div className={editMode ? dashed : ""} data-testid={`logo-color-usage-${block.id}`}>
      <LogoBlockHeader c={c} editMode={editMode} titlePlaceholder="Kleurgebruik" textPlaceholder="Toegestane kleur-combinaties voor het logo" onUpdate={onUpdate} />
      {noColors && (
        <div className="mb-5 rounded-md border border-amber-300 bg-amber-50 text-amber-900 px-4 py-3 text-sm" data-testid={`logo-color-usage-warning-${block.id}`}>
          Vul eerst de pagina <strong>Kleuren</strong> met je kleurenpalet voordat je combinaties kunt kiezen.
        </div>
      )}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
        {items.map((it, idx) => (
          <ColorUsageCard
            key={it.id || idx}
            item={it}
            colors={colors}
            logoSrc={src}
            logoIsSvg={isSvg}
            logoFileId={fileId}
            disabled={noColors}
            editMode={editMode}
            onUpdate={(p) => updateItem(idx, p)}
            onRemove={() => removeItem(idx)}
            testId={`logo-color-usage-card-${block.id}-${idx}`}
          />
        ))}
        {editMode && (
          <button
            onClick={addItem}
            data-testid={`logo-color-usage-add-${block.id}`}
            className="flex flex-col items-center justify-center gap-2 min-h-[280px] rounded-xl border-2 border-dashed border-gray-300 text-gray-400 hover:text-gray-600 text-sm"
          >
            <Plus className="w-5 h-5" /> Combinatie toevoegen
          </button>
        )}
      </div>
    </div>
  );
}

function ColorUsageCard({ item, colors, logoSrc, logoIsSvg, logoFileId, disabled, editMode, onUpdate, onRemove, testId }) {
  const bg = item.bg_color || "#f2f1ed";
  const fg = item.logo_color || "";
  // Gradients break SVG `fill=` directly; wrap the logo in a masked div so the
  // gradient becomes the "paint" through an SVG mask of the original logo.
  const fgIsGradient = typeof fg === "string" && fg.includes("gradient(");
  const bgIsGradient = typeof bg === "string" && bg.includes("gradient(");
  return (
    <div className="rounded-xl bg-white border border-gray-200 overflow-hidden flex flex-col" data-testid={testId}>
      <div
        className="relative aspect-square flex items-center justify-center p-6"
        style={bgIsGradient ? { backgroundImage: bg } : { background: bg }}
      >
        {logoSrc ? (
          logoIsSvg && fgIsGradient ? (
            // Mask the gradient with the SVG shape so the painting itself is the gradient.
            <div
              className="max-w-[70%] max-h-[70%] w-[70%] h-[70%]"
              style={{
                backgroundImage: fg,
                WebkitMaskImage: `url("${logoSrc}")`,
                maskImage: `url("${logoSrc}")`,
                WebkitMaskRepeat: "no-repeat",
                maskRepeat: "no-repeat",
                WebkitMaskPosition: "center",
                maskPosition: "center",
                WebkitMaskSize: "contain",
                maskSize: "contain",
              }}
              data-testid={`${testId}-gradient-logo`}
            />
          ) : logoIsSvg ? (
            <TintedSvgLogo fileId={logoFileId} color={fg} className="max-w-[70%] max-h-[70%]" />
          ) : (
            <img src={logoSrc} alt="logo preview" className="max-w-[70%] max-h-[70%] object-contain" />
          )
        ) : (
          <div className="text-xs text-[#1a1a1a]/40">Upload eerst de Primaire logo</div>
        )}
        {editMode && (
          <button onClick={onRemove} className="absolute top-2 left-2 w-7 h-7 rounded-md bg-white/95 border border-gray-300 text-gray-500 hover:text-red-600 flex items-center justify-center">
            <Trash2 className="w-3.5 h-3.5" />
          </button>
        )}
      </div>
      <div className={`px-4 py-4 space-y-3 ${disabled ? "opacity-50 pointer-events-none" : ""}`}>
        <SwatchRow label="Kleur van het logo" selected={fg} colors={colors} onPick={(hex) => onUpdate({ logo_color: hex })} editMode={editMode} />
        <SwatchRow label="Kleur van de achtergrond" selected={bg} colors={colors} onPick={(hex) => onUpdate({ bg_color: hex })} editMode={editMode} />
      </div>
    </div>
  );
}

// Fetches the primary SVG once, injects a *scoped* <style> block that only
// targets this specific inline SVG (via a per-instance data-tinted-id). Prior
// implementations used `svg * { fill: … }` which leaked into every other SVG
// on the page — Lucide icons, sibling Don'ts previews, etc.
function TintedSvgLogo({ fileId, color, className, outline }) {
  const [svg, setSvg] = useState("");
  const uid = useMemo(() => `tsvg-${Math.random().toString(36).slice(2, 10)}`, []);
  useEffect(() => {
    let active = true;
    if (!fileId) { setSvg(""); return undefined; }
    (async () => {
      try {
        const res = await fetch(fileUrl(fileId));
        const text = await res.text();
        if (active) setSvg(text);
      } catch { if (active) setSvg(""); }
    })();
    return () => { active = false; };
  }, [fileId]);
  const tinted = useMemo(() => {
    if (!svg) return "";
    // Always tag the SVG root so our scoped selector can find exactly this
    // instance — even when no colour is applied, so later retints stay local.
    const marked = svg.replace(/<svg\b([^>]*)>/i, (_m, a) => `<svg data-tinted-id="${uid}"${a}>`);
    if (outline) {
      // Outline mode: strip every fill, replace with a thin black stroke.
      // non-scaling-stroke keeps the line weight constant regardless of SVG
      // viewBox scaling so small logos still read as crisp outlines.
      const scope = `svg[data-tinted-id="${uid}"]`;
      const styleBlock = `<style>${scope} *{fill:none !important;stroke:#1a1a1a !important;stroke-width:1.5 !important;vector-effect:non-scaling-stroke;}</style>`;
      return marked.replace(/<svg\b([^>]*)>/i, (m, attrs) => `<svg${attrs}>${styleBlock}`);
    }
    if (!color) return marked;
    const scope = `svg[data-tinted-id="${uid}"]`;
    const styleBlock = `<style>${scope} *:not([fill="none"]){fill:${color} !important;}${scope} *:not([stroke="none"])[stroke]{stroke:${color} !important;}</style>`;
    const withStyle = marked.replace(/<svg\b([^>]*)>/i, (m, attrs) => `<svg${attrs}>${styleBlock}`);
    return withStyle
      .replace(/fill="(?!none)[^"]*"/gi, `fill="${color}"`)
      .replace(/stroke="(?!none)[^"]*"/gi, `stroke="${color}"`)
      .replace(/fill:\s*(?!none)[^;"}]+/gi, `fill:${color}`)
      .replace(/stroke:\s*(?!none)[^;"}]+/gi, `stroke:${color}`);
  }, [svg, color, uid, outline]);
  if (!tinted) return <div className={`${className} flex items-center justify-center text-xs text-gray-400`}>Laden…</div>;
  return <div className={`${className} flex items-center justify-center [&_svg]:w-full [&_svg]:h-full [&_svg]:object-contain`} dangerouslySetInnerHTML={{ __html: tinted }} />;
}

function SwatchRow({ label, selected, colors, onPick, editMode }) {
  // Each entry can be a solid hex *or* a gradient (any string containing
  // `gradient(`). We render both as 28×28 rounded-4 squares with a 1 px
  // #000 selection outline — gradients fill via background-image so CSS
  // handles the painting natively.
  const isGrad = (v) => typeof v === "string" && v.includes("gradient(");
  return (
    <div>
      <div className="text-[11px] uppercase tracking-wide text-gray-500 mb-1.5">{label}</div>
      <div className="flex flex-wrap gap-1.5">
        {(colors || []).map((col, i) => {
          const value = col.hex || col.value || "";
          const grad = isGrad(value);
          return (
            <button
              key={i}
              disabled={!editMode}
              onClick={() => onPick(value)}
              title={col.name || value}
              className={`w-7 h-7 rounded-[4px] transition ${selected === value ? "outline outline-1 outline-offset-[2px] outline-black" : "border border-gray-200"}`}
              style={grad ? { backgroundImage: value } : { background: value }}
            />
          );
        })}
      </div>
    </div>
  );
}

// ---------- Step 6: Don'ts ----------
const DONT_TRANSFORMS = {
  stretch:      { label: "Uitrekken",        style: { transform: "scaleX(1.5)" } },
  random_color: { label: "Willekeurige kleur", style: { filter: "hue-rotate(90deg) saturate(2)" } },
  rotate:       { label: "Roteren",          style: { transform: "rotate(18deg)" } },
  shadow:       { label: "Schaduw toevoegen", style: { filter: "drop-shadow(4px 6px 6px rgba(0,0,0,0.45))" } },
  // "outline" repaints the SVG so every shape loses its fill and becomes a
  // 1-weight stroke. Only works for vector logos; raster uploads show a badge.
  outline:      { label: "Outline maken",    style: null, outline: true },
  custom:       { label: "Iets anders",      style: null, custom: true },
};
const DONT_PLACEHOLDERS = {
  stretch:      "Geen uitrekken",
  random_color: "Geen willekeurige kleur",
  rotate:       "Geen roteren",
  shadow:       "Geen schaduw toevoegen",
  outline:      "Geen outline-versie van het logo gebruiken",
  custom:       "Beschrijf wat het logo niet mag doen",
};
const DONT_DEFAULT_PLACEHOLDER = "Beschrijf wat het logo niet mag doen";

export function LogoDontsBlock({ block, editMode, brand, pageBlocks, onUpdate }) {
  const c = block.content || {};
  const items = Array.isArray(c.items) ? c.items : [];
  const newId = () => (crypto.randomUUID ? crypto.randomUUID() : Math.random().toString(36).slice(2));

  const { src, isSvg, fileId } = usePrimaryLogo(pageBlocks);

  const addItem = () => onUpdate({ items: [...items, { id: newId(), transform: "", text: "" }] });
  const updateItem = (idx, patch) => onUpdate({ items: items.map((it, i) => (i === idx ? { ...it, ...patch } : it)) });
  const removeItem = (idx) => onUpdate({ items: items.filter((_, i) => i !== idx) });

  return (
    <div className={editMode ? dashed : ""} data-testid={`logo-donts-${block.id}`}>
      <LogoBlockHeader c={c} editMode={editMode} titlePlaceholder="Don'ts" textPlaceholder="Wat mag er niét met het logo gebeuren?" onUpdate={onUpdate} />
      <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
        {items.map((it, idx) => (
          <DontCard key={it.id || idx} item={it} logoSrc={src} logoIsSvg={isSvg} logoFileId={fileId} brand={brand} editMode={editMode} onUpdate={(p) => updateItem(idx, p)} onRemove={() => removeItem(idx)} testId={`logo-donts-card-${block.id}-${idx}`} />
        ))}
        {editMode && (
          <button
            onClick={addItem}
            data-testid={`logo-donts-add-${block.id}`}
            className="flex flex-col items-center justify-center gap-2 min-h-[280px] rounded-xl border-2 border-dashed border-gray-300 text-gray-400 hover:text-gray-600 text-sm"
          >
            <Plus className="w-5 h-5" /> Don't toevoegen
          </button>
        )}
      </div>
    </div>
  );
}

function DontCard({ item, logoSrc, logoIsSvg, logoFileId, brand, editMode, onUpdate, onRemove, testId }) {
  const kind = (item.transform && DONT_TRANSFORMS[item.transform]) || null;
  const inputRef = useRef();
  const [busy, setBusy] = useState(false);
  const upload = async (file) => {
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post(`/upload?brand_id=${brand.id}`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      onUpdate({ file_id: data.file_id, filename: data.filename });
    } catch { toast.error("Uploaden mislukt"); }
    finally { setBusy(false); }
  };
  const customSrc = item.file_id ? fileUrl(item.file_id) : null;
  const isCustom = kind?.custom;
  const isOutline = kind?.outline;
  const style = kind?.style || undefined;

  let preview;
  if (isCustom) {
    preview = customSrc
      ? <img src={customSrc} alt="don't" className="max-w-[70%] max-h-[70%] object-contain" />
      : editMode
        ? <button onClick={() => inputRef.current?.click()} className="flex flex-col items-center gap-1 text-xs text-gray-500 hover:text-gray-700"><UploadCloud className="w-5 h-5" /><span>Upload je eigen beeld</span></button>
        : <div className="text-xs text-gray-400">Geen beeld</div>;
  } else if (!logoSrc) {
    preview = <div className="text-xs text-gray-400">Upload eerst de Primaire logo</div>;
  } else if (isOutline && !logoIsSvg) {
    // Outline is a vector-only effect; raster uploads can't be re-stroked
    // reliably in the browser, so we show a clear "unavailable" badge rather
    // than ship a half-broken preview.
    preview = (
      <div className="flex flex-col items-center gap-2 text-xs text-gray-500 text-center px-4" data-testid="donts-outline-unavailable">
        <span className="px-2 py-1 rounded-md bg-gray-100 border border-gray-200 font-medium text-[11px]">Niet beschikbaar</span>
        <span>Outline-weergave werkt alleen op SVG-logo's.</span>
      </div>
    );
  } else {
    // Base (uncoloured) version of the Primaire logo — explicit empty color
    // isolates the Don'ts render from any Kleurgebruik swatch selection.
    const inner = logoIsSvg
      ? <TintedSvgLogo fileId={logoFileId} color="" outline={!!isOutline} className="w-full h-full" />
      : <img src={logoSrc} alt={kind?.label || "logo"} className="w-full h-full object-contain" />;
    preview = (
      <div className="relative flex items-center justify-center w-[70%] h-[70%]" style={style}>
        {inner}
        {kind?.badge && <span className="absolute -top-1 -right-3 text-base font-medium text-black">{kind.badge}</span>}
      </div>
    );
  }

  return (
    <div className="rounded-xl bg-white border border-gray-200 overflow-hidden flex flex-col" data-testid={testId}>
      <div className="relative aspect-square bg-[#f8f8f6] flex items-center justify-center overflow-hidden p-6">
        {preview}
        {editMode && (
          <>
            <input ref={inputRef} type="file" hidden accept={LOGO_UPLOAD_ACCEPT} onChange={(e) => upload(e.target.files?.[0])} />
            <button onClick={onRemove} className="absolute top-2 left-2 w-7 h-7 rounded-md bg-white/95 border border-gray-300 text-gray-500 hover:text-red-600 flex items-center justify-center">
              <Trash2 className="w-3.5 h-3.5" />
            </button>
            {isCustom && customSrc && (
              <button onClick={() => inputRef.current?.click()} disabled={busy} className="absolute top-2 right-2 px-2 h-7 rounded-md bg-white/95 border border-gray-300 text-xs">{busy ? "Bezig…" : "Vervang"}</button>
            )}
          </>
        )}
      </div>
      <div className="px-4 py-3 space-y-2">
        {editMode ? (
          <>
            <select
              value={item.transform || ""}
              onChange={(e) => onUpdate({ transform: e.target.value, ...(e.target.value === "custom" ? {} : { file_id: null }) })}
              className="bh-select w-full h-9 px-3 border border-gray-200 rounded-md bg-white text-sm"
              data-testid={`${testId}-select`}
            >
              <option value="">Maak een keuze</option>
              {Object.entries(DONT_TRANSFORMS).map(([k, v]) => (
                <option key={k} value={k}>{v.label}</option>
              ))}
            </select>
            <div className="flex items-center gap-2">
              <X className="w-4 h-4 text-black shrink-0" strokeWidth={2.25} />
              <Input value={item.text || ""} onChange={(e) => onUpdate({ text: e.target.value })} className="bg-white border-gray-200 text-sm" placeholder={DONT_PLACEHOLDERS[item.transform] || DONT_DEFAULT_PLACEHOLDER} />
            </div>
          </>
        ) : (
          <div className="flex items-start gap-2 text-sm text-[#1a1a1a]/70">
            <X className="w-4 h-4 text-black shrink-0 mt-0.5" strokeWidth={2.25} />
            <span>{item.text || DONT_PLACEHOLDERS[item.transform] || DONT_DEFAULT_PLACEHOLDER}</span>
          </div>
        )}
      </div>
    </div>
  );
}
